import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Save, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { API } from '@/config';

export const BankSettingsPanel = () => {
  const [iban, setIban] = useState('');
  const [beneficiary, setBeneficiary] = useState('');
  const [bankName, setBankName] = useState('');
  const [swiftBic, setSwiftBic] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/payment-settings`);
        setIban(data.iban || '');
        setBeneficiary(data.beneficiary || '');
        setBankName(data.bank_name || '');
        setSwiftBic(data.swift_bic || '');
        setPaymentNotes(data.payment_notes || '');
      } catch (e) {
        console.error(e);
        setError('Σφάλμα φόρτωσης ρυθμίσεων');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API}/admin/payment-settings`, {
        iban: iban.trim(),
        beneficiary: beneficiary.trim(),
        bank_name: bankName.trim(),
        swift_bic: swiftBic.trim(),
        payment_notes: paymentNotes.trim(),
      });
      setSavedAt(new Date());
      setTimeout(() => setSavedAt(null), 3000);
    } catch (e) {
      console.error(e);
      setError('Αποτυχία αποθήκευσης');
    } finally {
      setSaving(false);
    }
  };

  const isConfigured = !!iban.trim();

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="bank-settings-panel">
      <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-gold" />
            Τραπεζικά Στοιχεία
          </h2>
          {isConfigured ? (
            <span className="px-3 py-1 bg-green-500/15 text-green-400 border border-green-500/30 rounded-full text-xs flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Ενεργό
            </span>
          ) : (
            <span className="px-3 py-1 bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-full text-xs flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Δεν έχει ρυθμιστεί
            </span>
          )}
        </div>
        <p className="text-slate-400 text-sm mb-6">
          Αυτά τα στοιχεία θα εμφανίζονται στους πελάτες όταν επιλέγουν τραπεζική μεταφορά για πληρωμή. <strong className="text-gold">0% fees</strong> για εσάς.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              IBAN <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder="CY17 0020 0128 0000 0012 0052 7600"
              className="w-full px-3 py-2 bg-navy-200 border border-slate-700 rounded-lg text-white focus:border-gold focus:outline-none font-mono"
              data-testid="bank-iban-input"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Δικαιούχος / Beneficiary <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={beneficiary}
              onChange={(e) => setBeneficiary(e.target.value)}
              placeholder="J. House of Secretary"
              className="w-full px-3 py-2 bg-navy-200 border border-slate-700 rounded-lg text-white focus:border-gold focus:outline-none"
              data-testid="bank-beneficiary-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Όνομα Τράπεζας</label>
              <input
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="Bank of Cyprus"
                className="w-full px-3 py-2 bg-navy-200 border border-slate-700 rounded-lg text-white focus:border-gold focus:outline-none"
                data-testid="bank-name-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">SWIFT / BIC</label>
              <input
                type="text"
                value={swiftBic}
                onChange={(e) => setSwiftBic(e.target.value)}
                placeholder="BCYPCY2N"
                className="w-full px-3 py-2 bg-navy-200 border border-slate-700 rounded-lg text-white focus:border-gold focus:outline-none font-mono"
                data-testid="bank-swift-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Παρατηρήσεις Πληρωμής (προαιρετικά)
            </label>
            <textarea
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              rows={3}
              placeholder="π.χ. Εναλλακτικά μπορείτε να μεταφέρετε στη Zen card μου: TAG @jhouse2026"
              className="w-full px-3 py-2 bg-navy-200 border border-slate-700 rounded-lg text-white focus:border-gold focus:outline-none resize-none"
              data-testid="bank-notes-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Εδώ μπορείτε να προσθέσετε εναλλακτικούς τρόπους πληρωμής (Zen card, Revolut tag, κλπ.)
            </p>
          </div>

          {error && (
            <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-300">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <div className="text-xs text-slate-500">
              {savedAt && (
                <span className="text-green-400 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Αποθηκεύτηκε στις {savedAt.toLocaleTimeString('el-GR')}
                </span>
              )}
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-gradient-to-r from-gold to-amber-600 text-navy-200 font-bold rounded-lg hover:from-amber-500 hover:to-gold disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="bank-settings-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Αποθήκευση
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankSettingsPanel;
